/* HC - cart telemetry - 2026-07-04
   Lightweight on-device cart timing breadcrumbs for diagnosing add-to-cart slowness.
   queue: waitMs, depth
   depth = ops enqueued-and-unsettled at drain time (includes later-enqueued ops), not queue position; waitMs is the primary signal.
   add:net: ms, url, status, rtDuration, rtTtfb, rtTransferSize, serverTiming, actionId, wasHidden
   update:net: ms, url, status, rtDuration, rtTtfb, rtTransferSize, serverTiming, actionId, wasHidden
   NOTE: ms is Date.now() across the fetch .then(), so a hidden/suspended tab inflates it; trust rtDuration/serverTiming for true latency and use wasHidden/vis to filter.
   every event also carries: vis (v/h/p = visible/hidden/prerender at emit)
   challenge:armed: count
   challenge:rearmed: count
   challenge:cleared: activeForMs
   write:suppressed: none
   read:suppressed: none
   drawer:optimistic-open: none
   add:error: shown(inline|toast|self|none), msg — self = callsite rendered its own error UI; none = true silent failure
   qty:reconciled: mismatch, ms - mismatch means the optimistic estimate did not survive authority
   line:coalesced: merged, keys - merged = line writes absorbed after the batch was ready to send (each would have claimed its own queue slot before coalescing); keys = distinct line keys in the dispatched batch
   cg:queue: pending, inFlight
   cg:parked: none
   savedcart:refused: status, count, msg — one line of a saved-cart/reorder load the store refused; count = quantity asked, msg = the store's reason
   savedcart:limited: count, waitMs — the account line validator answered 429; count = lines in the check, waitMs = its Retry-After
   filter:apply: src, n, total, ms, cached — HC collection filters v2 (assets/ib-filters.js): one applied render; src = what
     triggered it (pop|sheet|toggle|chips|sort|popstate|more|more-auto), n = active filters after it, total = result count, cached = served from the in-page cache
   filter:empty: n — a filtered view landed on zero results; n = active filters
   Filter events share this buffer with the cart events it exists for, so at most MAX_FILTER_EVENTS of them are kept
   (oldest dropped first): a long browse never pushes the cart events out. */
(function() {
  'use strict';

  var root = typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : null);
  if (!root) {
    return;
  }

  var MAX_EVENTS = 300;
  var MAX_FILTER_EVENTS = 50;
  var FLUSH_SIZE = 25;
  var BEACON_EVENT_LIMIT = 50;
  var BEACON_MAX_BYTES = 30000;
  var BEACON_RETRY_BACKOFF_MS = 10000;
  var PERSIST_DELAY_MS = 500;
  var STORAGE_KEY = 'ib_cart_telemetry';
  var DEBUG_KEY = 'ib_cart_telemetry_debug';
  var hasOwn = Object.prototype.hasOwnProperty;
  var EVENTS = {
    queue: ['waitMs', 'depth'],
    'add:net': ['ms', 'url', 'status', 'rtDuration', 'rtTtfb', 'rtTransferSize', 'serverTiming', 'actionId', 'wasHidden'],
    'update:net': ['ms', 'url', 'status', 'rtDuration', 'rtTtfb', 'rtTransferSize', 'serverTiming', 'actionId', 'wasHidden'],
    'challenge:armed': ['count'],
    'challenge:rearmed': ['count'],
    'challenge:cleared': ['activeForMs'],
    'write:suppressed': [],
    'read:suppressed': [],
    'drawer:optimistic-open': [],
    'add:error': ['shown', 'msg'],
    'qty:reconciled': ['mismatch', 'ms'],
    'line:coalesced': ['merged', 'keys'],
    'cg:queue': ['pending', 'inFlight'],
    'cg:parked': [],
    'savedcart:refused': ['status', 'count', 'msg'],
    'savedcart:limited': ['count', 'waitMs'],
    'filter:apply': ['src', 'n', 'total', 'ms', 'cached'],
    'filter:empty': ['n']
  };
  var NUMERIC_FIELDS = { waitMs: true, depth: true, ms: true, count: true, activeForMs: true, pending: true,
    status: true, rtDuration: true, rtTtfb: true, rtTransferSize: true, merged: true, keys: true, n: true, total: true };
  var STRING_LIMITS = { msg: 80, path: 100, url: 120, serverTiming: 160, actionId: 24 };
  var ENUM_FIELDS = {
    shown: { inline: true, toast: true, self: true, none: true },
    src: { pop: true, sheet: true, toggle: true, chips: true, sort: true, popstate: true, more: true, 'more-auto': true }
  };
  var BOOLEAN_FIELDS = { inFlight: true, mismatch: true, wasHidden: true, cached: true };
  var sid = makeSid();
  var buffer = loadBuffer();
  var pending = [];
  var persistTimer = null;
  var beaconBackoffUntil = 0;

  function makeSid() {
    try {
      var id = '';
      while (id.length < 8) {
        id += Math.random().toString(36).slice(2);
      }
      return id.slice(0, 8);
    } catch (error) {
      return String(new Date().getTime()).slice(-8);
    }
  }

  function now() {
    try {
      return Date.now ? Date.now() : new Date().getTime();
    } catch (error) {
      return 0;
    }
  }

  function storageGet(key) {
    try {
      if (root.localStorage && root.localStorage.getItem) {
        return root.localStorage.getItem(key);
      }
    } catch (error) {}
    return null;
  }

  function storageSet(key, value) {
    try {
      if (root.localStorage && root.localStorage.setItem) {
        root.localStorage.setItem(key, value);
      }
    } catch (error) {}
  }

  function loadBuffer() {
    try {
      var stored = storageGet(STORAGE_KEY);
      var parsed = stored ? JSON.parse(stored) : [];
      return Array.isArray(parsed) ? capFilterEvents(parsed.slice(-MAX_EVENTS)) : [];
    } catch (error) {
      return [];
    }
  }

  function persistNow() {
    try {
      storageSet(STORAGE_KEY, JSON.stringify(buffer.slice(-MAX_EVENTS)));
    } catch (error) {}
  }

  function schedulePersist() {
    var timerId;
    if (persistTimer !== null) {
      return;
    }
    if (typeof setTimeout !== 'function') {
      persistNow();
      return;
    }
    timerId = setTimeout(function() {
      if (persistTimer !== timerId) {
        return;
      }
      persistTimer = null;
      persistNow();
    }, PERSIST_DELAY_MS);
    persistTimer = timerId;
  }

  function persistFinalState() {
    if (persistTimer !== null && typeof clearTimeout === 'function') {
      clearTimeout(persistTimer);
    }
    persistTimer = null;
    persistNow();
  }

  function challengeState() {
    var active = false;
    var count = 0;
    try {
      var cart = root.theme && root.theme.cart;
      // Passive snapshot only: telemetry reads must never invoke challenge
      // methods because their lazy-expiry path can mutate the cart state.
      active = Boolean(cart && cart.challenge && cart.challenge.active === true);
      if (cart && cart.challenge && typeof cart.challenge.count === 'number') {
        count = cart.challenge.count;
      }
    } catch (error) {}
    return {
      active: active,
      count: count
    };
  }

  function pathname() {
    try {
      return root.location && root.location.pathname ? root.location.pathname : '';
    } catch (error) {
      return '';
    }
  }

  // v2: page visibility at emit time, as a 1-char code (v=visible, h=hidden,
  // p=prerender). A cart write emitting 'h' means its `ms` was inflated by the
  // tab being backgrounded, not by a slow server.
  function visibility() {
    try {
      var state = root.document && root.document.visibilityState;
      return state ? String(state).charAt(0) : '';
    } catch (error) {
      return '';
    }
  }

  function endpoint() {
    try {
      var url = root.theme && root.theme.settings && root.theme.settings.cartTelemetryUrl;
      return typeof url === 'string' && url.replace(/\s/g, '') ? url : '';
    } catch (error) {
      return '';
    }
  }

  function debug(event) {
    try {
      if (storageGet(DEBUG_KEY) === '1' && root.console && root.console.log) {
        root.console.log('[IBCartTelemetry]', event);
      }
    } catch (error) {}
  }

  function isFilterEvent(event) {
    return Boolean(event) && String(event.type).indexOf('filter:') === 0;
  }

  // Drops the oldest filter events beyond MAX_FILTER_EVENTS, in place (the
  // buffer array is the one IBCartTelemetry.buffer exposes).
  function capFilterEvents(list) {
    var kept = 0;
    var index;
    for (index = list.length - 1; index >= 0; index -= 1) {
      if (isFilterEvent(list[index])) {
        kept += 1;
        if (kept > MAX_FILTER_EVENTS) {
          list.splice(index, 1);
        }
      }
    }
    return list;
  }

  function trim(list) {
    var dropped = 0;
    while (list.length > MAX_EVENTS) {
      list.shift();
      dropped += 1;
    }
    return dropped;
  }

  function flush() {
    var sent = false;
    try {
      var url = endpoint();
      var chunkSize;
      var chunk;
      var payload;
      if (!url || !pending.length || !root.navigator || typeof root.navigator.sendBeacon !== 'function') {
        return false;
      }
      if (now() < beaconBackoffUntil) {
        return false;
      }
      while (pending.length) {
        chunkSize = Math.min(BEACON_EVENT_LIMIT, pending.length);
        chunk = pending.slice(0, chunkSize);
        payload = JSON.stringify({
          v: 1,
          sid: sid,
          events: chunk
        });
        while (chunk.length && payload.length > BEACON_MAX_BYTES) {
          if (chunkSize === 1) {
            pending.shift();
            debug({ type: 'beacon:trimmed', dropped: 1 });
            chunkSize = Math.min(BEACON_EVENT_LIMIT, pending.length);
          } else {
            chunkSize = Math.max(1, Math.floor(chunkSize / 2));
          }
          chunk = pending.slice(0, chunkSize);
          payload = JSON.stringify({
            v: 1,
            sid: sid,
            events: chunk
          });
        }
        if (!chunk.length) {
          return sent;
        }
        if (!root.navigator.sendBeacon(url, payload)) {
          beaconBackoffUntil = now() + BEACON_RETRY_BACKOFF_MS;
          return false;
        }
        beaconBackoffUntil = 0;
        pending = pending.slice(chunk.length);
        sent = true;
      }
    } catch (error) {}
    return sent;
  }

  function sanitizeValue(key, value) {
    if (hasOwn.call(NUMERIC_FIELDS, key)) {
      return +value;
    }
    if (hasOwn.call(ENUM_FIELDS, key)) {
      return hasOwn.call(ENUM_FIELDS[key], String(value)) ? String(value) : 'none';
    }
    if (hasOwn.call(STRING_LIMITS, key)) {
      return String(value).slice(0, STRING_LIMITS[key]);
    }
    if (hasOwn.call(BOOLEAN_FIELDS, key)) {
      return value === true;
    }
    // Future EVENTS fields not registered in a type map get stringified+bounded, never raw.
    return String(value).slice(0, 80);
  }

  function payloadFor(type, data) {
    var eventType = String(type);
    var payload = {};
    var fields;
    var index;
    var key;

    if (!hasOwn.call(EVENTS, eventType)) {
      payload.type = 'unknown';
      payload.orig = eventType.slice(0, 40);
      return payload;
    }

    payload.type = eventType;
    fields = EVENTS[eventType];
    if (data && typeof data === 'object') {
      for (index = 0; index < fields.length; index += 1) {
        key = fields[index];
        if (hasOwn.call(data, key)) {
          payload[key] = sanitizeValue(key, data[key]);
        }
      }
    }

    return payload;
  }

  function getEventDetail(evt) {
    return evt && evt.detail && typeof evt.detail === 'object' ? evt.detail : {};
  }

  function trackUnsafe(type, data) {
    var state = challengeState();
    var payload = payloadFor(type, data);
    var dropped;
    var event = {
      v: 1,
      t: now(),
      type: payload.type
    };
    var key;

    for (key in payload) {
      if (hasOwn.call(payload, key) && key !== 'type') {
        event[key] = payload[key];
      }
    }

    event.challengeActive = state.active;
    event.challengeCount = state.count;
    event.path = sanitizeValue('path', pathname());
    event.vis = visibility();
    event.sid = sid;

    buffer.push(event);
    pending.push(event);
    // Before trim, so a filter event over its cap never costs a cart event.
    if (isFilterEvent(event)) {
      capFilterEvents(buffer);
      capFilterEvents(pending);
    }
    trim(buffer);
    dropped = trim(pending);
    if (dropped) {
      debug({ type: 'beacon:trimmed', dropped: dropped });
    }
    schedulePersist();
    debug(event);
    if (pending.length >= FLUSH_SIZE) {
      flush();
    }

    return event;
  }

  root.IBCartTelemetry = {
    buffer: buffer,
    track: function(type, data) {
      try {
        return trackUnsafe(type, data);
      } catch (error) {
        return null;
      }
    },
    dump: function() {
      try {
        return JSON.stringify(buffer.slice(-MAX_EVENTS));
      } catch (error) {
        return '[]';
      }
    }
  };

  try {
    // v2: enlarge the Resource Timing buffer so a slow/suspended cart write's
    // /cart/*.js entry isn't evicted (this store loads 250+ resources) before
    // the write's .then() runs and reads rtDuration/serverTiming — otherwise the
    // slowest events lose exactly the true-latency data we need most.
    if (root.performance && typeof root.performance.setResourceTimingBufferSize === 'function') {
      root.performance.setResourceTimingBufferSize(500);
    }
  } catch (error) {}

  try {
    if (root.addEventListener) {
      root.addEventListener('pagehide', function() {
        persistFinalState();
        flush();
      });
    }
    if (root.document && root.document.addEventListener) {
      root.document.addEventListener('cart:add:error', function(evt) {
        var detail = getEventDetail(evt);
        root.IBCartTelemetry.track('add:error', {
          shown: detail.renderedInline ? 'inline' : (detail.renderedToast ? 'toast' : (detail.selfRendered ? 'self' : 'none')),
          msg: String(detail.message || '').slice(0, 60)
        });
      });

      root.document.addEventListener('visibilitychange', function() {
        try {
          if (root.document.visibilityState === 'hidden') {
            persistFinalState();
            flush();
          }
        } catch (error) {}
      });
    }
  } catch (error) {}
})();
